import { useEffect, useState } from 'react';

export type GeolocationPermissionState = 'granted' | 'denied' | 'prompt' | 'unsupported';

/**
 * useGeolocationPermission
 * Lightweight hook returning the current geolocation permission state and keeping it updated.
 * - Subscribes to navigator.permissions (if available) and handles onchange updates
 * - Falls back to a persisted local flag when Permissions API is unavailable
 * Returns one of: 'granted' | 'denied' | 'prompt' | 'unsupported'
 */
export const useGeolocationPermission = (): GeolocationPermissionState => {
  const [state, setState] = useState<GeolocationPermissionState>('unsupported');

  useEffect(() => {
    let cancelled = false;
    let permStatus: PermissionStatus | null = null;

    const readLocalDenied = () => {
      try {
        if (typeof window !== 'undefined' && localStorage.getItem('geoPermDenied') === '1') {
          return 'denied' as GeolocationPermissionState;
        }
      } catch {
        // ignore
      }
      return 'unsupported' as GeolocationPermissionState;
    };

    const run = async () => {
      try {
        if (!('permissions' in navigator) || typeof navigator.permissions?.query !== 'function') {
          // Permissions API not supported: infer from persisted flag
          const fallback = readLocalDenied();
          if (!cancelled) setState(fallback);
          return;
        }

        // Cast to a descriptor type that works across TS DOM lib versions
        type GeoDescriptor = { name: 'geolocation' };
        permStatus = await navigator.permissions.query({ name: 'geolocation' });

        if (!cancelled) setState((permStatus.state) ?? 'unsupported');

        // Keep state in sync on changes and persist denial
        permStatus.onchange = () => {
          if (cancelled) return;
          const newState = (permStatus!.state as GeolocationPermissionState) ?? 'unsupported';
          setState(newState);
          try {
            if (newState === 'denied') localStorage.setItem('geoPermDenied', '1');
          } catch {
            // ignore
          }
        };
      } catch {
        // Query failed: fallback to local storage flag
        const fallback = readLocalDenied();
        if (!cancelled) setState(fallback);
      }
    };

    void run();
    return () => {
      cancelled = true;
      if (permStatus) permStatus.onchange = null;
    };
  }, []);

  return state;
};
